import { csrfToken } from "../../../shared/csrf"

interface LoginProps {
  // null on a clean visit or a reload; a fixed string for exactly the one
  // rendered login page that follows a rejected or failed attempt
  // (FR-12) — Rails flash's one-shot lifecycle, not anything this
  // component tracks itself.
  error: string | null
  // The trip's own title once a trip record exists (data, rendered
  // verbatim — FR-7), or the `identity.login.heading` locale fallback on
  // an unseeded database (FR-6).
  heading: string
  // Login renders outside Layout (FR-3) and so does not read the shared
  // `ui` data — its own chrome strings are resolved server-side from
  // config/locales/{en,ru}.yml and arrive as page props instead.
  subtitle: string
  signInButton: string
  footnote: string
}

// The application's one unauthenticated page, restyled in t007a against
// the owner-approved design contract (docs/design/login-page.html —
// implemented verbatim: classes, element order, the mt-8/mt-4 difference
// between the clean and error artboards). Renders outside the shared
// Layout (FR-3): no trip navigation, tabs or sign-out control belong on a
// signed-out visitor's page.
export default function Login({ error, heading, subtitle, signInButton, footnote }: LoginProps) {
  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-6 pb-30">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="text-2xl font-semibold leading-8 text-gray-900">{heading}</h1>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>

      {/* FR-11: conditional banner, no reserved space when `error` is
          absent. A plain <p> — page content on a fresh document load, not
          a live region (NFR-4) — so assistive technology reads it like
          any other text. */}
      {error && (
        <div className="mt-8 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          {/* Decorative: carries no information the text beside it does
              not (NFR-4). */}
          <svg aria-hidden="true" className="mt-px shrink-0 text-red-700" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <circle cx="8" cy="11" r="0.9" fill="currentColor" />
          </svg>
          <p className="text-left text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* A real browser POST, not an Inertia request (see
          app/shared/csrf.ts): omniauth-rails_csrf_protection rejects a
          GET-initiated OAuth flow, and Google's top-level redirect needs a
          fresh document to return to (FR-10, FR-24 — mechanics untouched
          by the restyle). No focus-ring suppression, so the button keeps
          the user agent's visible keyboard focus indicator (NFR-4). */}
      <form method="post" action="/auth/google_oauth2" className={error ? "mt-4" : "mt-8"}>
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <button
          type="submit"
          className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl border border-gray-300 bg-white text-[15px] font-medium text-gray-900"
        >
          {/* Google's official four-color "G" mark, neither recolored nor
              distorted (FR-8) — decorative, its label text says the same
              thing (NFR-4). */}
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.616z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          {signInButton}
        </button>
      </form>

      <p className="mt-4 text-center text-[13px] leading-[18px] text-gray-400">{footnote}</p>
    </main>
  )
}
